import { Modal } from "@mui/material";
import { RotateCcw, X, Settings, ChevronDown, AlertTriangle, Search } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { showError } from "../../utils/toast";

interface RescanData {
    agent_id: string;
    job_type: string;
    parameters: any;
    client_id: string;
}

interface RescanModalProps {
    open: boolean;
    onClose: () => void;
    agent: any;
    onSubmit: (data: RescanData) => void;
    customJobTypes?: { value: string; label: string; }[];
}

const JOB_TYPES = [
    { value: 'asset_discovery', label: 'Asset Discovery' },
    { value: 'local_asset_scan', label: 'Local Asset Scan' },
    { value: 'camera_scan', label: 'Camera Discovery' },
    { value: 'snmp_scan', label: 'SNMP Discovery' },
    { value: 'firewall_scan', label: 'Firewall' },
    { value: 'ilo', label: 'ILO' }
];

const RescanModal: React.FC<RescanModalProps> = ({ open, onClose, agent, onSubmit, customJobTypes }) => {
    const displayJobTypes = customJobTypes || JOB_TYPES;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedJobType, setSelectedJobType] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Update dropdown position when it opens
    useEffect(() => {
        if (showDropdown && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width
            });
        }
    }, [showDropdown]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const selectJobType = (jobType: string) => {
        setSelectedJobType(jobType);
        setShowDropdown(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedJobType) {
            showError('Please select a job type');
            return;
        }

        setIsSubmitting(true);

        try {
            const rescanData: RescanData = {
                client_id: agent?.client_id,
                agent_id: agent?.id,
                job_type: selectedJobType,
                parameters: {}, // Parameters are now empty as per your request to remove the form
            };

            onSubmit(rescanData);
            handleClose();
        } catch (error) {
            console.error('Rescan failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setSelectedJobType('');
        setShowDropdown(false);
        setIsSubmitting(false);
        onClose();
    };

    const getStatusBg = (status: string) => {
        switch (status) {
            case 'online': return 'bg-green-400';
            case 'offline': return 'bg-gray-400';
            case 'busy': return 'bg-yellow-400';
            case 'error': return 'bg-red-400';
            default: return 'bg-gray-400';
        }
    };

    const selectedJob = displayJobTypes.find(job => job.value === selectedJobType);

    return (
        <Modal open={open} onClose={handleClose}>
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                w-[95%] sm:w-[450px] max-w-md bg-white dark:bg-gray-900/95 backdrop-blur-xl 
                border border-gray-200 dark:border-orange-400/20 shadow-2xl rounded-xl text-gray-900 dark:text-white
                relative z-[9997]">

                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent"></div>

                <form onSubmit={handleSubmit} className="p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center">
                            <div className="mr-3 text-orange-500 dark:text-orange-400">
                                <RotateCcw
                                    className={`h-6 w-6 ${isSubmitting ? 'animate-spin' : ''}`}
                                />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                {agent?.last_job_completed_at ? 'Rescan Agent' : 'Scan Agent'}
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="space-y-6">
                        {/* Agent Info */}
                        <div className="bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700/50">
                            <div className="flex items-center gap-3">
                                <div className={`w-3 h-3 ${getStatusBg(agent?.status || 'offline')} rounded-full shadow-[0_0_8px_rgba(74,222,128,0.5)]`}></div>
                                <div>
                                    <p className="text-gray-900 dark:text-white font-medium text-sm">{agent?.name}</p>
                                    <p className="text-gray-500 dark:text-gray-400 text-xs font-mono">{agent?.ip_address}</p>
                                </div>
                            </div>
                        </div>

                        {/* Job Type Selection */}
                        <div className="relative">
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                <Settings className="w-4 h-4 mr-2 text-orange-500 dark:text-orange-400" />
                                Select Scan Type <span className='text-red-500 ml-1'>*</span>
                            </label>

                            <div className="relative" ref={dropdownRef}>
                                <button
                                    ref={buttonRef}
                                    type="button"
                                    onClick={() => setShowDropdown(!showDropdown)}
                                    className={`w-full bg-white dark:bg-gray-800/50 border ${!selectedJobType ? 'border-gray-300 dark:border-gray-700' : 'border-orange-500/40 dark:border-orange-400/30'} 
                                        rounded-lg px-4 py-3 text-gray-900 dark:text-white text-left
                                        focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 
                                        transition-all duration-200 flex items-center justify-between group`}
                                >
                                    <span className={!selectedJobType ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white font-medium'}>
                                        {selectedJob ? selectedJob.label : 'Choose a scan job...'}
                                    </span>
                                    <ChevronDown
                                        className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${showDropdown ? 'rotate-180 text-orange-500 dark:text-orange-400' : 'group-hover:text-gray-600 dark:group-hover:text-white'}`}
                                    />
                                </button>

                                {showDropdown && createPortal(
                                    <>
                                        <div className="fixed inset-0 z-[99998]" onClick={() => setShowDropdown(false)} />
                                        <div
                                            className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                                                rounded-lg shadow-2xl z-[99999] max-h-60 overflow-y-auto
                                                scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600"
                                            style={{
                                                top: `${dropdownPosition.top}px`,
                                                left: `${dropdownPosition.left}px`,
                                                width: `${dropdownPosition.width}px`
                                            }}
                                        >
                                            {displayJobTypes.map((jobType, index) => (
                                                <button
                                                    key={jobType.value}
                                                    type="button"
                                                    onClick={() => selectJobType(jobType.value)}
                                                    className={`w-full text-left px-4 py-3 transition-colors
                                                        ${index < displayJobTypes.length - 1 ? 'border-b border-gray-100 dark:border-gray-700/50' : ''} 
                                                        ${selectedJobType === jobType.value ? 'bg-orange-500/10 dark:bg-orange-600/20 text-orange-600 dark:text-orange-400 font-medium' : 'text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-orange-600/10'}
                                                        first:rounded-t-lg last:rounded-b-lg text-sm`}
                                                >
                                                    {jobType.label}
                                                </button>
                                            ))}
                                        </div>
                                    </>,
                                    document.body
                                )}
                            </div>

                            {!selectedJobType && (
                                <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 flex items-center italic">
                                    <AlertTriangle className="w-3 h-3 mr-1 text-orange-500/60 dark:text-orange-400/50" />
                                    Pick a scan type to initiate the agent process
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex justify-end gap-3 mt-8 pt-5 border-t border-gray-200 dark:border-gray-800">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !selectedJobType}
                            className="flex items-center gap-2 px-8 py-2 bg-gradient-to-r from-orange-600 to-orange-700 
                                text-white rounded-lg font-bold hover:from-orange-700 hover:to-orange-800 
                                hover:shadow-[0_0_20px_rgba(234,88,12,0.3)] transition-all duration-300 
                                disabled:opacity-30 disabled:cursor-not-allowed min-w-[120px] justify-center text-sm"
                        >
                            {isSubmitting ? (
                                <>
                                    <RotateCcw className="w-4 h-4 animate-spin" />
                                    Starting...
                                </>
                            ) : (
                                <>
                                    <Search className="w-4 h-4" />
                                    Start Scan
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default RescanModal;
